import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {SuggestionService} from '@twpub/core/services/suggestion.service';
import {DictionaryService} from '@twpub/core/services/dictionary.service';
import {FeatureFlagService} from '@twpub/core/services';
import {SuggestionStatus, TermSuggestion} from '@twpub/core/models/term-suggestion';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {DatePipe} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import {
  ReviewCommentDialogComponent,
  ReviewCommentDialogData,
  ReviewCommentDialogResult
} from '../review-comment-dialog/review-comment-dialog.component';

@Component({
  selector: 'pub-suggestions-panel',
  templateUrl: './suggestions-panel.component.html',
  styleUrls: ['./suggestions-panel.component.scss'],
  standalone: true,
  imports: [
    MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell,
    MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow,
    MatIcon, MatTooltip,
    MatSelect, MatOption, MatFormField, MatLabel,
    DatePipe
  ]
})
export class SuggestionsPanelComponent implements OnInit {
  displayedColumns: string[] = ['termName', 'languageCode', 'dictionary', 'submitterName', 'createdAt', 'status'];
  suggestions: TermSuggestion[] = [];
  statusFilter: SuggestionStatus | 'ALL' = 'PENDING';
  private dictNamesById: Map<number, string> = new Map();

  sendFailedTooltip = $localize`:@@suggestions.sendFailed.tooltip:Could not send to TermWeb — click the row to retry`;

  private msgLoadFailed = $localize`:@@suggestions.error.load:Failed to load suggestions`;
  private msgApproved = $localize`:@@suggestions.msg.approved:Approved`;
  private msgApprovedSendFailed = $localize`:@@suggestions.msg.approvedSendFailed:Approved, but could not send to TermWeb — click the row to retry`;
  private msgDenied = $localize`:@@suggestions.msg.denied:Denied`;
  private msgSent = $localize`:@@suggestions.msg.sent:Sent to TermWeb`;
  private msgSendStillFailing = $localize`:@@suggestions.msg.sendStillFailing:Still could not send to TermWeb`;
  private msgApproveFailed = $localize`:@@suggestions.error.approve:Failed to approve suggestion`;
  private msgDenyFailed = $localize`:@@suggestions.error.deny:Failed to deny suggestion`;
  private msgSendFailed = $localize`:@@suggestions.error.send:Failed to send suggestion to TermWeb`;

  constructor(
    private suggestionService: SuggestionService,
    private dictionaryService: DictionaryService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private featureFlagService: FeatureFlagService,
    private router: Router
  ) {
  }

  ngOnInit(): void {
    // Defense in depth: the menu link is hidden when the flag is off, but a direct
    // URL still reaches us. Bail out instead of issuing suggestion API calls that
    // would fail on instances without the feature.
    this.featureFlagService.waitForReady(() => {
      if (!this.featureFlagService.isFeatureEnabled('TermSuggestions', false)) {
        this.router.navigate(['/admin/home']);
        return;
      }
      this.dictionaryService.getDictionaries(true).subscribe(dicts => {
        this.dictNamesById = new Map(dicts.map(d => [d.id, d.name]));
      });
      this.loadSuggestions();
    });
  }

  dictionaryName(s: TermSuggestion): string {
    if (s.dictionaryId == null) {
      return '—';
    }
    return this.dictNamesById.get(s.dictionaryId) ?? String(s.dictionaryId);
  }

  loadSuggestions(): void {
    const status = this.statusFilter === 'ALL' ? undefined : this.statusFilter;
    this.suggestionService.list(status).subscribe({
      next: (data) => this.suggestions = data,
      error: () => this.showMessage(this.msgLoadFailed)
    });
  }

  onStatusFilterChange(value: SuggestionStatus | 'ALL'): void {
    this.statusFilter = value;
    this.loadSuggestions();
  }

  /** A row is actionable if the admin can approve/deny (pending) or retry the TermWeb send (approved + sendFailed). */
  canReview(s: TermSuggestion): boolean {
    return this.isPending(s) || this.canRetrySend(s);
  }

  onRowClick(s: TermSuggestion): void {
    if (!this.canReview(s)) return;
    const data: ReviewCommentDialogData = {
      termName: s.termName,
      canApproveDeny: this.isPending(s),
      canRetrySend: this.canRetrySend(s)
    };
    const dialogRef = this.dialog.open(ReviewCommentDialogComponent, {data, width: '400px'});
    dialogRef.afterClosed().subscribe((result: ReviewCommentDialogResult | undefined) => {
      if (!result) return;
      switch (result.action) {
        case 'approve': this.doApprove(s, result.comment); break;
        case 'deny':    this.doDeny(s, result.comment);    break;
        case 'send':    this.doSendToTermWeb(s);           break;
      }
    });
  }

  private doApprove(s: TermSuggestion, comment: string | undefined): void {
    this.suggestionService.approve(s.id, comment).subscribe({
      next: (updated) => {
        this.showMessage(updated.sendFailed
          ? `${this.msgApprovedSendFailed}: "${s.termName}"`
          : `${this.msgApproved}: "${s.termName}"`);
        this.loadSuggestions();
      },
      error: () => this.showMessage(this.msgApproveFailed)
    });
  }

  private doDeny(s: TermSuggestion, comment: string | undefined): void {
    this.suggestionService.deny(s.id, comment).subscribe({
      next: () => {
        this.showMessage(`${this.msgDenied}: "${s.termName}"`);
        this.loadSuggestions();
      },
      error: () => this.showMessage(this.msgDenyFailed)
    });
  }

  private doSendToTermWeb(s: TermSuggestion): void {
    this.suggestionService.sendToTermWeb(s.id).subscribe({
      next: (updated) => {
        this.showMessage(updated.sendFailed
          ? `${this.msgSendStillFailing}: "${s.termName}"`
          : `${this.msgSent}: "${s.termName}"`);
        this.loadSuggestions();
      },
      error: () => this.showMessage(this.msgSendFailed)
    });
  }

  private isPending(suggestion: TermSuggestion): boolean {
    return suggestion.status === 'PENDING';
  }

  /** Approved suggestion whose last TermWeb send failed — admin can retry. */
  private canRetrySend(suggestion: TermSuggestion): boolean {
    return suggestion.status === 'APPROVED' && !!suggestion.sendFailed;
  }

  private showMessage(msg: string): void {
    this.snackBar.open(msg, undefined, {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }
}
